<template>
  <Teleport to="#modalTarget">
    <div
      class="modal fade show d-block"
      tabindex="-1"
      style="background-color: rgba(0, 0, 0, 0.5)"
      v-if="show"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <font-awesome-icon icon="file-invoice" class="me-2" />
              Rechnungsdaten verwalten
            </h5>
            <button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
          </div>

          <div class="modal-body">
            <!-- Fehlermeldung -->
            <div v-if="error" class="alert alert-danger" role="alert">
              <font-awesome-icon icon="exclamation-triangle" class="me-2" />
              {{ error }}
            </div>

            <!-- Erfolgsmeldung -->
            <div v-if="success" class="alert alert-success" role="alert">
              <font-awesome-icon icon="check-circle" class="me-2" />
              {{ success }}
            </div>

            <!-- BillingForm Komponente -->
            <BillingForm
              :billing-data="billingData"
              :is-free-upgrade="false"
              :show-tax-fields="true"
              :show-save-option="false"
              :show-card="false"
              @billing-data-changed="onBillingDataChanged"
            />
          </div>

          <div class="modal-footer">
            <div class="d-flex justify-content-between w-100">
              <div>
                <button
                  v-if="hasExistingData"
                  type="button"
                  class="btn btn-danger"
                  @click="handleDelete"
                  :disabled="loading"
                >
                  <font-awesome-icon icon="trash" class="me-1" />
                  Löschen
                </button>
              </div>
              <div>
                <button
                  type="button"
                  class="btn btn-secondary me-2"
                  @click="closeModal"
                  :disabled="loading"
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  class="btn btn-primary"
                  @click="handleSubmit"
                  :disabled="loading"
                >
                  <span
                    v-if="loading"
                    class="spinner-border spinner-border-sm me-2"
                    role="status"
                  ></span>
                  <font-awesome-icon v-else icon="save" class="me-1" />
                  {{ hasExistingData ? 'Aktualisieren' : 'Speichern' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'
import { getInvoiceData, saveInvoiceData, deleteInvoiceData } from '@/api/invoiceData'
import { showSuccess, showError, showConfirmDialog } from '@/services/notification'
import BillingForm from './BillingForm.vue'

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['close', 'saved'])

const loading = ref(false)
const error = ref('')
const success = ref('')
const hasExistingData = ref(false)

const billingData = ref({
  firstName: '',
  lastName: '',
  company: '',
  street: '',
  number: '',
  zip: '',
  city: '',
  country: 'Deutschland',
  email: '',
  vatId: '',
  taxId: '',
  saveForFuture: true,
})

const closeModal = () => {
  emit('close')
  resetForm()
}

const resetForm = () => {
  error.value = ''
  success.value = ''
  billingData.value = {
    firstName: '',
    lastName: '',
    company: '',
    street: '',
    number: '',
    zip: '',
    city: '',
    country: 'Deutschland',
    email: '',
    vatId: '',
    taxId: '',
    saveForFuture: true,
  }
}

const loadInvoiceData = async () => {
  try {
    const response = await getInvoiceData()
    if (response.success && response.data.invoiceData) {
      const data = response.data.invoiceData
      billingData.value = {
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        company: data.company || data.companyName || '',
        street: data.street || '',
        number: data.number || data.houseNumber || '',
        zip: data.zip || data.zipCode || data.postalCode || '',
        city: data.city || '',
        country: data.country || 'Deutschland',
        email: data.email || '',
        vatId: data.vatId || '',
        taxId: data.taxId || '',
        saveForFuture: true,
      }
      hasExistingData.value = true
    } else {
      hasExistingData.value = false
    }
  } catch (err) {
    console.error('Fehler beim Laden der Rechnungsdaten:', err)
    hasExistingData.value = false
  }
}

const onBillingDataChanged = (newBillingData) => {
  billingData.value = { ...billingData.value, ...newBillingData }
}

const handleSubmit = async () => {
  try {
    loading.value = true
    error.value = ''
    success.value = ''

    // Convert to API format
    const apiData = {
      firstName: billingData.value.firstName,
      lastName: billingData.value.lastName,
      companyName: billingData.value.company,
      street: billingData.value.street,
      houseNumber: billingData.value.number,
      postalCode: billingData.value.zip,
      city: billingData.value.city,
      country: billingData.value.country,
      email: billingData.value.email,
      vatId: billingData.value.vatId,
      taxId: billingData.value.taxId,
    }

    const response = await saveInvoiceData(apiData)

    if (response.success) {
      success.value = response.message
      hasExistingData.value = true
      emit('saved', response.data.invoiceData)

      setTimeout(() => {
        success.value = ''
      }, 3000)
    }
  } catch (err) {
    console.error('Fehler beim Speichern der Rechnungsdaten:', err)
    error.value =
      err.response?.data?.error || err.message || 'Fehler beim Speichern der Rechnungsdaten'
  } finally {
    loading.value = false
  }
}

const handleDelete = async () => {
  const result = await showConfirmDialog(
    'Rechnungsdaten löschen',
    'Möchtest du die Rechnungsdaten wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
    'Ja, löschen',
    'Abbrechen',
    { icon: 'warning', iconColor: '#dc3545' },
  )

  if (!result.isConfirmed) {
    return
  }

  try {
    loading.value = true
    error.value = ''
    success.value = ''

    const response = await deleteInvoiceData()

    if (response.success) {
      success.value = response.message
      hasExistingData.value = false
      resetForm()
      emit('saved', null)

      setTimeout(() => {
        success.value = ''
      }, 3000)
    }
  } catch (err) {
    console.error('Fehler beim Löschen der Rechnungsdaten:', err)
    error.value =
      err.response?.data?.error || err.message || 'Fehler beim Löschen der Rechnungsdaten'
  } finally {
    loading.value = false
  }
}

watch(
  () => props.show,
  (newValue) => {
    if (newValue) {
      loadInvoiceData()
    } else {
      resetForm()
    }
  },
)

const setError = (errorMessage) => {
  error.value = errorMessage
}

defineExpose({
  setError,
})
</script>

<style scoped>
.modal {
  z-index: 1055;
}

.modal-dialog {
  margin-top: 5vh;
}

.alert {
  margin-bottom: 1rem;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}
</style>
